// src/pages/InexistenciaFirmar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { apiConfig } from "../config/api.config";
import CertInexistenciaTemplate from "../components/CertInexistenciaTemplate.jsx";

const safeParseJSON = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeJson = async (resp) => {
  const txt = await resp.text().catch(() => "");
  try {
    return txt ? JSON.parse(txt) : {};
  } catch {
    return { raw: txt };
  }
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const firstOrSelf = (x) => (Array.isArray(x) ? x[0] : x);

function getRoleIdFromStorage() {
  const storedUser = safeParseJSON(localStorage.getItem("auth_user"));
  const raw =
    storedUser?.role ??
    storedUser?.role_id ??
    storedUser?.rol ??
    storedUser?.rolId ??
    storedUser?.roleId ??
    storedUser?.rol_id ??
    storedUser?.roleName ??
    "";

  const s = String(raw).trim().toLowerCase();

  if (/^\d+$/.test(s)) return Number(s);

  if (s.includes("secret")) return 10;
  if (s.includes("jefe")) return 10;
  if (s.includes("revisor")) return 9;
  if (s.includes("validador")) return 9;
  if (s.includes("planea")) return 9;

  return 9;
}

function getRoleLabel(roleId) {
  if (roleId === 10) return "Secretario (Rol 10)";
  return "Validador (Rol 9)";
}

function sanitizeFilePart(v) {
  return String(v || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 140);
}

/** Espera a que todas las imágenes dentro del nodo estén cargadas (clave para que el PDF NO salga en blanco). */
async function waitForImages(rootEl, timeoutMs = 12000) {
  if (!rootEl) return;

  const imgs = Array.from(rootEl.querySelectorAll("img"));
  if (!imgs.length) return;

  const start = Date.now();

  await Promise.all(
    imgs.map((img) => {
      return new Promise((resolve) => {
        const done = () => resolve();

        // Ya cargada
        if (img.complete && img.naturalWidth > 0) return done();

        // decode() ayuda mucho en Chrome
        if (typeof img.decode === "function") {
          img
            .decode()
            .then(done)
            .catch(() => done());
        }

        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });

        // Failsafe timeout por imagen
        const t = setInterval(() => {
          if (Date.now() - start > timeoutMs) {
            clearInterval(t);
            done();
          }
        }, 250);
      });
    })
  );
}

export default function InexistenciaFirmar() {
  const { id } = useParams();
  const navigate = useNavigate();

  const roleId = useMemo(() => getRoleIdFromStorage(), []);
  const roleLabel = useMemo(() => getRoleLabel(roleId), [roleId]);

  const [loading, setLoading] = useState(true);
  const [solicitud, setSolicitud] = useState(null);
  const [error, setError] = useState("");

  const [firmaFile, setFirmaFile] = useState(null);
  const [firmaB64, setFirmaB64] = useState("");
  const [accionLoading, setAccionLoading] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState("");

  const docRef = useRef(null);

  const detailUrl = useMemo(() => {
    return apiConfig.getUrl(
      `/api/inexistencia/solicitudes?id=${encodeURIComponent(id || "")}`
    );
  }, [id]);

  const respondUrl = useMemo(() => {
    return apiConfig.getUrl(`/api/inexistencia/solicitudes/respond/index.php`);
  }, []);

  // ✅ Upload correcto de inexistencia
  const uploadInexistenciaUrl = useMemo(() => {
    const ep =
      apiConfig?.endpoints?.uploadInexistenciaAttachment ||
      "/api/inexistencia/solicitudes/upload/index.php";
    return apiConfig.getUrl(ep);
  }, []);

  const canSign = useMemo(() => {
    if (!solicitud) return false;

    const currentStep = Number(solicitud?.current_step ?? 9);
    const status = (solicitud?.status || "").toLowerCase();

    if (status === "finalizado" || status === "approved" || currentStep >= 11) return false;

    if (roleId === 9) return currentStep === 9;
    if (roleId === 10) return currentStep === 10;

    return false;
  }, [roleId, solicitud]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!id) {
        setLoading(false);
        setError("Falta el id de la solicitud.");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const resp = await fetch(detailUrl, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status} ${txt}`);
        }

        const raw = await resp.json();
        const s = firstOrSelf(raw);

        if (!alive) return;
        if (!s) throw new Error("No se encontró la solicitud (respuesta vacía).");

        let nextSolicitud = { ...s };

        if (!nextSolicitud.objeto_snapshot && nextSolicitud.objeto) {
          nextSolicitud.objeto_snapshot = nextSolicitud.objeto;
        }

        if (!nextSolicitud.objeto_snapshot) {
          nextSolicitud.objeto_snapshot =
            nextSolicitud.objeto_contractual || nextSolicitud.descripcion || "";
        }

        setSolicitud(nextSolicitud);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "No se pudo cargar la solicitud.");
        setSolicitud(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, detailUrl]);

  const onPickFirma = async (file) => {
    setFirmaFile(file || null);
    setFirmaB64("");
    if (!file) return;
    const b64 = await toBase64(file);
    setFirmaB64(b64);
  };

  const buildPdfFileName = () => {
    // Radicado (usa el mejor campo disponible sin inventar estructura)
    const rad =
      solicitud?.radicado ||
      solicitud?.radicado_num ||
      solicitud?.consecutivo ||
      solicitud?.numero ||
      solicitud?.code ||
      solicitud?.codigo ||
      "";

    const radPart = rad ? sanitizeFilePart(rad) : `ID_${sanitizeFilePart(id || "")}`;

    // Secretaría (fijo como pediste)
    const secPart = sanitizeFilePart("Secretaria_Servicios_Administrativos");

    return `inexistencia_${radPart}_${secPart}.pdf`;
  };

  const generatePdfBlob = async () => {
    if (!docRef.current) {
      throw new Error("No se encontró el contenido del certificado para generar PDF.");
    }

    // ✅ CLAVE: esperar imágenes (logo + firma) antes de pdf
    await waitForImages(docRef.current);

    const filename = buildPdfFileName();

    const opt = {
      margin: 10,
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      },
      jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
    };

    const blob = await html2pdf().set(opt).from(docRef.current).output("blob");
    return { blob, filename };
  };

  // ✅ Subir firma (archivo real) a /api/inexistencia/solicitudes/upload/index.php
  const uploadFirmaFile = async () => {
    if (!firmaFile) throw new Error("Debes seleccionar una firma (archivo).");

    const filename = `firma_${roleId}_${Date.now()}.png`;
    const fd = new FormData();
    fd.append("file", firmaFile);
    fd.append("kind", "firma");
    fd.append("requestId", String(id || ""));
    fd.append("roleId", String(roleId));
    fd.append("fileName", filename);

    const upResp = await fetch(uploadInexistenciaUrl, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    const upData = await safeJson(upResp);
    if (!upResp.ok) {
      throw new Error(
        upData?.error ||
          upData?.message ||
          upData?.raw ||
          `Error subiendo firma (HTTP ${upResp.status})`
      );
    }
    if (!upData?.url) throw new Error("El servidor no devolvió la URL de la firma.");
    return upData.url;
  };

  // ✅ Subir PDF (solo rol 10)
  const uploadPdfBlob = async ({ blob, filename }) => {
    const fd = new FormData();
    fd.append("file", new File([blob], filename, { type: "application/pdf" }));
    fd.append("kind", "pdf");
    fd.append("requestId", String(id || ""));
    fd.append("roleId", String(roleId));
    fd.append("fileName", filename);

    const upResp = await fetch(uploadInexistenciaUrl, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    const upData = await safeJson(upResp);
    if (!upResp.ok) {
      throw new Error(
        upData?.error || upData?.message || upData?.raw || `Error subiendo PDF (HTTP ${upResp.status})`
      );
    }
    if (!upData?.url) throw new Error("El servidor no devolvió la URL del PDF.");
    return upData.url;
  };

  const downloadPdf = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const aprobar = async () => {
    if (!firmaFile || !firmaB64) {
      alert("Debes cargar la firma antes de aprobar.");
      return;
    }

    setAccionLoading(true);
    try {
      // ✅ 1) Subir firma como archivo y obtener URL pública
      const firmaUrl = await uploadFirmaFile();

      // ✅ 2) Si rol 10: generar PDF (con la firma del secretario ya pintada) y subir
      let pdfUrl = "";
      let shouldDownloadPdf = false;
      let pdfFilename = "";

      if (roleId === 10) {
        // Espera un tick para que React pinte la firma en el template antes del PDF
        await new Promise((r) => setTimeout(r, 0));

        const { blob, filename } = await generatePdfBlob();
        pdfFilename = filename;
        pdfUrl = await uploadPdfBlob({ blob, filename });
        shouldDownloadPdf = true;
      }

      // ✅ 3) Responder: mandamos URL (lo que se debe guardar y luego renderizar)
      const payload = {
        requestId: id,
        action: "aprobar",
        roleId: roleId,       // no rompe si backend lo ignora
        firmaUrl,             // ✅ GUARDAR ESTO EN BD
        firmaBase64: firmaB64, // compat (si backend aún la acepta)
        pdfUrl,
        notes: "",
      };

      const resp = await fetch(respondUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await safeJson(resp);
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || data?.raw || `HTTP ${resp.status}`);
      }

      if (shouldDownloadPdf && pdfUrl) {
        alert("Certificado firmado y finalizado exitosamente. Descargando PDF...");
        setTimeout(() => {
          downloadPdf(pdfUrl, pdfFilename || `inexistencia_${id}.pdf`);
        }, 500);
      } else {
        alert("Firma guardada correctamente. La solicitud avanza a la siguiente fase.");
      }

      setTimeout(() => {
        navigate("/inexistencia/solicitudes", { replace: true });
      }, shouldDownloadPdf ? 2000 : 1500);
    } catch (e) {
      console.error(e);
      alert(e?.message || "No se pudo aprobar.");
    } finally {
      setAccionLoading(false);
    }
  };

  const rechazar = async () => {
    if (!motivoRechazo.trim()) {
      alert("Debes escribir el motivo del rechazo.");
      return;
    }

    setAccionLoading(true);
    try {
      const payload = {
        requestId: id,
        action: "rechazar",
        notes: motivoRechazo.trim(),
      };

      const resp = await fetch(respondUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await safeJson(resp);
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || data?.raw || `HTTP ${resp.status}`);
      }

      alert("Rechazado correctamente.");
      navigate("/inexistencia/solicitudes", { replace: true });
    } catch (e) {
      console.error(e);
      alert(e?.message || "No se pudo rechazar.");
    } finally {
      setAccionLoading(false);
    }
  };

  if (loading) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>Cargando solicitud...</div>;
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 16 }}>
        <div style={{ maxWidth: 640, width: "100%", background: "white", borderRadius: 12, padding: 16 }}>
          <h3 style={{ margin: 0, color: "#b91c1c" }}>No se pudo cargar</h3>
          <p style={{ color: "#374151" }}>{error}</p>
          <button
            onClick={() => navigate("/inexistencia/solicitudes")}
            style={{
              marginTop: 8,
              background: "#1e40af",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Volver a solicitudes
          </button>
        </div>
      </div>
    );
  }

  const dataForTemplate = solicitud || {};
  const currentStep = Number(solicitud?.current_step ?? 9);
  const isFinalized = currentStep >= 11 || (solicitud?.status || "").toLowerCase() === "approved";

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 24 }}>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          background: "white",
          borderRadius: 16,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: 16,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/inexistencia/solicitudes")}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              background: "#111827",
              color: "white",
              cursor: "pointer",
            }}
          >
            Volver
          </button>

          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1e3a8a" }}>
            Certificación de Inexistencia — {roleLabel}
          </h2>

          <div />
        </div>

        <div style={{ padding: "12px 18px", background: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
          <p style={{ margin: 0, fontSize: 14, color: "#1e40af" }}>
            <strong>Estado actual:</strong>{" "}
            {currentStep === 9 && "Pendiente firma del Validador"}
            {currentStep === 10 && "Pendiente firma del Secretario"}
            {currentStep >= 11 && "Certificado finalizado"}
          </p>
        </div>

        <div style={{ padding: 18 }}>
          <div ref={docRef}>
            <CertInexistenciaTemplate
              data={dataForTemplate}
              firmaPreview={firmaB64}
              roleId={roleId}
            />
          </div>

          {isFinalized ? (
            <div style={{ marginTop: 18, padding: 16, background: "#dcfce7", borderRadius: 10, textAlign: "center" }}>
              <p style={{ margin: 0, color: "#166534", fontWeight: 700, fontSize: 16 }}>✓ Certificado finalizado</p>
              {solicitud?.pdf_url && (
                <button
                  onClick={() => window.open(solicitud.pdf_url, "_blank")}
                  style={{
                    marginTop: 12,
                    padding: "10px 16px",
                    background: "#1e40af",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Descargar PDF
                </button>
              )}
            </div>
          ) : canSign ? (
            <div
              style={{
                marginTop: 18,
                paddingTop: 18,
                borderTop: "1px solid #e5e7eb",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                alignItems: "start",
              }}
            >
              <div>
                <h3 style={{ margin: "0 0 8px 0" }}>Cargar firma</h3>
                <input type="file" accept="image/*" onChange={(e) => onPickFirma(e.target.files?.[0] || null)} />
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
                  {firmaB64 ? "Firma cargada." : "Sube una imagen (firma) para aprobar."}
                </div>
              </div>

              <div>
                <h3 style={{ margin: "0 0 8px 0" }}>Decisión</h3>

                {roleId === 10 && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: 10,
                      background: "#fef3c7",
                      borderRadius: 8,
                      border: "1px solid #fbbf24",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 13, color: "#92400e" }}>
                      <strong>Importante:</strong> Al firmar como Secretario, el certificado se finalizará automáticamente y se descargará el PDF.
                    </p>
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={aprobar}
                    disabled={accionLoading}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "none",
                      background: "#16a34a",
                      color: "white",
                      cursor: accionLoading ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      opacity: accionLoading ? 0.6 : 1,
                    }}
                  >
                    {accionLoading ? "Procesando…" : roleId === 10 ? "Firmar y Finalizar" : "Aprobar"}
                  </button>

                  <button
                    type="button"
                    onClick={rechazar}
                    disabled={accionLoading}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "none",
                      background: "#dc2626",
                      color: "white",
                      cursor: accionLoading ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      opacity: accionLoading ? 0.6 : 1,
                    }}
                  >
                    {accionLoading ? "Procesando…" : "Rechazar"}
                  </button>
                </div>

                <div style={{ marginTop: 12 }}>
                  <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
                    Motivo de rechazo (obligatorio si rechazas)
                  </label>
                  <textarea
                    value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    rows={4}
                    placeholder="Escribe el motivo."
                    style={{
                      width: "100%",
                      borderRadius: 10,
                      border: "1px solid #e5e7eb",
                      padding: 10,
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 18, padding: 16, background: "#f3f4f6", borderRadius: 10, textAlign: "center" }}>
              <p style={{ margin: 0, color: "#6b7280" }}>No tienes permiso para firmar en esta fase del proceso.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
